import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  advanceTransferStatus,
  isTransferInFlight,
  type OffRampRail,
  type OffRampRecipient,
  type OffRampTransfer,
  type OffRampTransferStatus,
} from './off-ramp.types';

interface OffRampState {
  rails: OffRampRail[];
  railsLoaded: boolean;
  railsErrorKey: string | null;
  recipients: OffRampRecipient[];
  recipientsLoaded: boolean;
  /** One list slot for the whole slice — the history screen is its only consumer. */
  transfers: OffRampTransfer[];
  transfersCount: number;
  transfersLoading: boolean;
  /** Transfer the progress screen is watching; polled while in flight. */
  trackedTransferId: string | null;
  trackedErrorKey: string | null;
}

const initialState: OffRampState = {
  rails: [],
  railsLoaded: false,
  railsErrorKey: null,
  recipients: [],
  recipientsLoaded: false,
  transfers: [],
  transfersCount: 0,
  transfersLoading: false,
  trackedTransferId: null,
  trackedErrorKey: null,
};

export const OffRampStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    sepaRail: computed(
      () => store.rails().find(rail => rail.rail === 'SEPA') ?? store.rails()[0] ?? null,
    ),
    defaultRecipient: computed(
      () => store.recipients().find(recipient => recipient.isDefault) ?? null,
    ),
    trackedTransfer: computed(
      () => store.transfers().find(transfer => transfer.id === store.trackedTransferId()) ?? null,
    ),
    hasInFlight: computed(() =>
      store.transfers().some(transfer => isTransferInFlight(transfer.status)),
    ),
  })),
  withMethods(store => ({
    setRails(rails: OffRampRail[]): void {
      patchState(store, { rails, railsLoaded: true, railsErrorKey: null });
    },
    setRailsError(railsErrorKey: string): void {
      patchState(store, { railsErrorKey, railsLoaded: true });
    },
    setRecipients(recipients: OffRampRecipient[]): void {
      patchState(store, { recipients, recipientsLoaded: true });
    },
    upsertRecipient(recipient: OffRampRecipient): void {
      const known = store.recipients().some(r => r.id === recipient.id);
      const next = known
        ? store.recipients().map(r => (r.id === recipient.id ? recipient : r))
        : [...store.recipients(), recipient];
      // The backend keeps at most one default per rail — mirror that locally.
      const recipients = recipient.isDefault
        ? next.map(r =>
            r.id !== recipient.id && r.rail === recipient.rail && r.isDefault
              ? { ...r, isDefault: false }
              : r,
          )
        : next;
      patchState(store, { recipients });
    },
    removeRecipient(id: string): void {
      patchState(store, { recipients: store.recipients().filter(r => r.id !== id) });
    },
    setTransfersLoading(transfersLoading: boolean): void {
      patchState(store, { transfersLoading });
    },
    setTransfers(transfers: OffRampTransfer[], count = transfers.length): void {
      patchState(store, { transfers, transfersCount: count, transfersLoading: false });
    },
    /** Merge a polled page without rewinding statuses a WS event already advanced. */
    mergeTransfers(transfers: OffRampTransfer[]): void {
      const current = new Map(store.transfers().map(t => [t.id, t]));
      const merged = transfers.map(t => {
        const known = current.get(t.id);
        return known ? { ...t, status: advanceTransferStatus(known.status, t.status) } : t;
      });
      // The polled page *is* the list — keeping leftovers from other pages
      // would show them as extra rows on this one. The single exception is the
      // transfer the progress screen watches: dropping it would blank that
      // screen mid-poll.
      const trackedId = store.trackedTransferId();
      const tracked =
        trackedId === null || merged.some(t => t.id === trackedId)
          ? undefined
          : current.get(trackedId);
      patchState(store, { transfers: tracked ? [...merged, tracked] : merged });
    },
    upsertTransfer(transfer: OffRampTransfer): void {
      const known = store.transfers().find(t => t.id === transfer.id);
      const next = known
        ? { ...transfer, status: advanceTransferStatus(known.status, transfer.status) }
        : transfer;
      patchState(store, {
        transfers: known
          ? store.transfers().map(t => (t.id === transfer.id ? next : t))
          : [next, ...store.transfers()],
        trackedErrorKey: store.trackedTransferId() === transfer.id ? null : store.trackedErrorKey(),
      });
    },
    patchTransferStatus(transferId: string, status: OffRampTransferStatus): void {
      patchState(store, {
        transfers: store
          .transfers()
          .map(t =>
            t.id === transferId ? { ...t, status: advanceTransferStatus(t.status, status) } : t,
          ),
      });
    },
    setTracked(trackedTransferId: string | null): void {
      patchState(store, { trackedTransferId, trackedErrorKey: null });
    },
    setTrackedError(trackedErrorKey: string): void {
      patchState(store, { trackedErrorKey });
    },
    reset(): void {
      patchState(store, initialState);
    },
  })),
);
